import React, { useRef, useEffect } from 'react'
import styled from 'styled-components'
import { space, color, background } from 'styled-system'
import themeGet from '@styled-system/theme-get'
import { transparentize } from 'polished'
import TextareaAutosize from '@bonobolabs/react-autosize-textarea'

const inputWithNoStyleProps = React.forwardRef(
  ({ fontWeight, fontSize, color, px, py, bg, ...props }, ref) => (
    <TextareaAutosize {...props} ref={ref} />
  ),
)

const StyledInput = styled(inputWithNoStyleProps)`
  font: inherit;
  ${space};
  ${color};
  ${background};
  width: 100%;
  border: none;
  resize: none;
  line-height: 1.25;
  &:focus {
    /* We're using background colour to emphasise focus */
    outline: none;
  }
  &::placeholder {
    color: ${(props) =>
      transparentize(
        0.6,
        themeGet('colors.' + props.color, props.color)(props),
      )};
  }
`

const EditableTitle = React.forwardRef(
  ({ title, color, bg, ...props }, ref) => (
    <StyledInput
      color={color}
      bg={bg}
      defaultValue={title}
      ref={ref}
      {...props}
    />
  ),
)

export const NewActionTitle = ({
  bg,
  color,
  onSubmit,
  onCancel,
  defaultTitle = null,
  placeholder = null,
}) => {
  const inputRef = useRef()

  useEffect(() => {
    // add slight delay to make focus work when page loads
    setTimeout(() => {
      const ref = inputRef.current
      if (!ref) return
      ref.focus()
      // select at end
      ref.setSelectionRange(ref.value.length, ref.value.length)
      // does what it says on the can
      // not in firefox/ie
      if (typeof ref.scrollIntoViewIfNeeded === 'function') {
        ref.scrollIntoViewIfNeeded()
      } else if (typeof ref.scrollIntoView === 'function') {
        ref.scrollIntoView()
      }
    }, 5)
  }, [])

  useEffect(() => {
    // The autosize height is measured on mount. If that runs before the web
    // font has loaded (or while the card's enter animation still has the field
    // narrow) the field is measured too tall and only corrects on the first
    // keystroke, causing a layout shift. Force a recompute once the fonts are
    // ready and after the enter animation has settled.
    // read the ref lazily - like the focus effect below, the textarea ref
    // isn't reliably set synchronously on mount
    const recompute = () => {
      const el = inputRef.current
      if (el) el.dispatchEvent(new Event('autosize:update'))
    }
    const timer = setTimeout(recompute, 250)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(recompute).catch(() => {})
    }
    return () => clearTimeout(timer)
  }, [])

  const getValue = () => inputRef.current.value.trim()

  const isEmpty = () => {
    return getValue().length === 0
  }

  const submit = () => {
    const newTitle = getValue()

    // Can't save an empty title
    if (isEmpty()) return onCancel()

    onSubmit(newTitle)
  }

  return (
    <EditableTitle
      ref={inputRef}
      color={color}
      bg={bg}
      onBlur={submit}
      placeholder={placeholder}
      title={defaultTitle}
      onKeyDown={(e) => {
        if (e.keyCode === 13) {
          // press enter
          e.preventDefault()

          submit()
        } else if (e.keyCode === 27) {
          // press escape
          onCancel()
        }
        // Prevent keys going up to Action
        // otherwise typing c would complete the Action
        e.stopPropagation()
      }}
    />
  )
}
